<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">
            {{ form._id ? "Editar Operador" : "Novo Operador" }}
          </h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Cadastre operadores, defina centro de custo e configure permissões de acesso.</p>
        </div>

        <router-link
          to="/operadores"
          class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
        >
          <svg class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Voltar
        </router-link>
      </div>

      <form class="space-y-6" autocomplete="off" @submit.prevent="save">
        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="border-b border-gray-100 px-6 py-5">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Informações do Operador</h2>
                <p class="mt-1 max-w-2xl text-sm text-gray-500">Preencha os dados principais do usuário e configure o vínculo operacional.</p>
              </div>

              <p class="max-w-md rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-500 shadow-sm">Operadores podem ter permissões específicas ou acesso administrativo completo.</p>
            </div>
          </div>

          <div class="p-6">
            <div class="grid grid-cols-12 gap-6">
              <div class="col-span-12 min-w-0 rounded-3xl border border-gray-100 bg-slate-50/70 p-5 md:col-span-7 md:p-6">
                <div class="grid grid-cols-1 gap-5">
                  <div class="flex flex-col gap-1.5">
                    <label for="nome" class="text-sm font-medium text-gray-500"> Nome </label>
                    <input id="nome" v-model="form.nome" type="text" placeholder="Nome do operador" :class="inputClass" />
                  </div>

                  <div class="flex flex-col gap-1.5">
                    <label for="email" class="text-sm font-medium text-gray-500"> Email* </label>
                    <input id="email" v-model="form.email" type="text" placeholder="email@empresa.com" :class="inputClass" />
                  </div>

                  <div class="flex flex-col gap-1.5">
                    <label for="telefone" class="text-sm font-medium text-gray-500"> Telefone </label>
                    <input id="telefone" v-model="form.telefone" type="number" placeholder="Telefone" :class="inputClass" />
                  </div>

                  <div v-if="$store.state.user.lzv2 && $store.state.user.lzv2.centrocusto" class="flex flex-col gap-1.5">
                    <label for="centrocusto" class="text-sm font-medium text-gray-500"> Centro de Custo </label>

                    <div class="relative group">
                      <select id="centrocusto" v-model="form.centrocusto" :required="!form.admin && !form.supervisor" :disabled="form.supervisor" :class="selectClass">
                        <option :value="null">{{ form.supervisor ? "Todos os centros de custos" : "Nenhum" }}</option>
                        <option v-for="c in centroscusto" :key="c._id" :value="c._id">
                          {{ c.nome }}
                        </option>
                      </select>

                      <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-span-12 min-w-0 rounded-3xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm md:col-span-5">
                <div class="border-b border-gray-100 pb-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Segurança</p>
                  <h3 class="mt-2 text-lg font-semibold text-gray-900">Senha do operador</h3>
                  <p class="mt-1 text-sm text-gray-500">Defina uma senha para novos operadores ou altere a senha de um cadastro existente.</p>
                </div>

                <div class="mt-5 space-y-4">
                  <button
                    v-if="!alterarPassword"
                    type="button"
                    class="h-11 w-full rounded-xl border border-blue-200 bg-blue-50 px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-100 hover:text-white hover:shadow-md"
                    @click="alterarPassword = true"
                  >
                    Alterar password
                  </button>

                  <div v-if="alterarPassword" class="space-y-4">
                    <div class="flex flex-col gap-1.5">
                      <label for="password" class="text-sm font-medium text-gray-500"> Senha </label>
                      <div class="relative">
                        <input id="password" v-model="form.password" autocomplete="new-password" :type="mostrarSenha ? 'text' : 'password'" placeholder="Digite a senha" :class="inputClass" />
                        <button type="button" @click="mostrarSenha = !mostrarSenha" class="absolute inset-y-0 right-3 flex items-center text-gray-300 transition hover:text-gray-500">
                          <svg v-if="!mostrarSenha" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0Z" />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7Z"
                            />
                          </svg>

                          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.584 10.587A2 2 0 0012 14a2 2 0 001.414-.586" />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9.879 5.583A10.01 10.01 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.964 9.964 0 01-2.043 3.368M6.228 6.228C4.521 7.352 3.229 9.015 2.458 12c1.274 4.057 5.065 7 9.542 7a9.98 9.98 0 004.132-.892"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div class="flex flex-col gap-1.5">
                      <label for="password2" class="text-sm font-medium text-gray-500"> Confirme a senha </label>
                      <div class="relative">
                        <input id="password2" v-model="form.password2" autocomplete="new-password" :type="mostrarSenhaConfirmacao ? 'text' : 'password'" placeholder="Confirme a senha" :class="inputClass" />
                        <button type="button" @click="mostrarSenhaConfirmacao = !mostrarSenhaConfirmacao" class="absolute inset-y-0 right-3 flex items-center text-gray-300 transition hover:text-gray-500">
                          <svg v-if="!mostrarSenhaConfirmacao" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0Z" />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7Z"
                            />
                          </svg>

                          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.584 10.587A2 2 0 0012 14a2 2 0 001.414-.586" />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9.879 5.583A10.01 10.01 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.964 9.964 0 01-2.043 3.368M6.228 6.228C4.521 7.352 3.229 9.015 2.458 12c1.274 4.057 5.065 7 9.542 7a9.98 9.98 0 004.132-.892"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-gray-500">
                      {{ alterarPassword ? "A senha será validada antes de salvar." : "A senha atual será mantida." }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="border-b border-gray-100 px-6 py-5">
            <h2 class="text-lg font-semibold text-gray-900">Acessos do Operador</h2>
            <p class="mt-1 text-sm text-gray-500">Configure quais áreas e ações estarão disponíveis para este operador.</p>
          </div>

          <div v-if="contaBluesend && !$store.state.user.centrocusto" class="px-6 pt-6" :class="{ 'pb-6': form.supervisor }">
            <label class="permission-card">
              <input id="supervisor" v-model="form.supervisor" type="checkbox" :class="checkboxClass" @change="setSupervisor" />
              <span><strong>Supervisor</strong><small>Todos os centros de custos e acessos do ADM, exceto Operadores e Configurações.</small></span>
            </label>
          </div>
          <div v-if="!form.supervisor" class="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            <label v-if="!$store.state.user.centrocusto" class="permission-card">
              <input id="admin" v-model="form.admin" type="checkbox" :class="checkboxClass" @change="setAdmin" />
              <span>
                <strong>Admin</strong>
                <small>{{ form.centrocusto ? "Visualização de todo o sistema vinculado ao centro de custo." : "Visualização de todo o sistema e todos os centros de custo." }}</small>
              </span>
            </label>

            <label v-if="$store.state.user.lzv2 && $store.state.user.lzv2.respostas" class="permission-card" :class="form.admin ? 'opacity-50' : ''">
              <input id="chatAcesso" v-model="form.chatAcesso" type="checkbox" :disabled="form.admin" :class="checkboxClass" @change="setchatAcesso" />
              <span>
                <strong>Acesso ao chat</strong>
                <small>Permite receber e enviar mensagens.</small>
              </span>
            </label>

            <label v-if="$store.state.user.lzv2 && $store.state.user.lzv2.respostas" class="permission-card" :class="form.admin ? 'opacity-50' : ''">
              <input
                id="visualizarFiltrosChat"
                v-model="form.visualizarFiltrosChat"
                type="checkbox"
                :disabled="form.admin"
                @input="() => (form.visualizarFiltrosChat = !form.visualizarFiltrosChat)"
                :class="checkboxClass"
              />
              <span>
                <strong>Filtros do chat</strong>
                <small>Acesso aos filtros de centro de custo e operador.</small>
              </span>
            </label>

            <label v-if="$store.state.user.lzv2 && $store.state.user.lzv2.respostas" class="permission-card">
              <input id="visualizarChat" v-model="form.visualizarChat" type="checkbox" :class="checkboxClass" @change="setvisualizarChat" />
              <span>
                <strong>Apenas visualizar chat</strong>
                <small>Supervisor operadores {{ !form.admin || form.centrocusto ? "— limitado ao centro de custo." : "" }}</small>
              </span>
            </label>

            <label v-if="$store.state.user.lzv2 && $store.state.user.lzv2.respostas" class="permission-card" :class="form.admin ? 'opacity-50' : ''">
              <input id="cpermitirMoverChat" v-model="form.permitirMoverChat" type="checkbox" :disabled="form.admin" :class="checkboxClass" />
              <span>
                <strong>Mover conversas</strong>
                <small>Permite mover conversas entre operadores.</small>
              </span>
            </label>

            <label class="permission-card" :class="form.admin ? 'opacity-50' : ''">
              <input id="uploadCampanha" v-model="form.uploadCampanha" type="checkbox" :disabled="form.admin" :class="checkboxClass" />
              <span>
                <strong>Upload de campanha</strong>
                <small>{{ !form.admin || form.centrocusto ? "Limitado ao centro de custo." : "Permite upload de campanhas." }}</small>
              </span>
            </label>

            <label class="permission-card" :class="form.admin ? 'opacity-50' : ''">
              <input id="rastrearAcesso" v-model="form.rastrearAcesso" type="checkbox" :disabled="form.admin" :class="checkboxClass" />
              <span>
                <strong>Rastrear envios</strong>
                <small>{{ !form.admin || form.centrocusto ? "Limitado ao centro de custo." : "Permite rastrear envios." }}</small>
              </span>
            </label>

            <label class="permission-card">
              <input id="relatorioAcesso" v-model="form.relatorioAcesso" type="checkbox" :class="checkboxClass" />
              <span>
                <strong>Relatórios</strong>
                <small>Permite acesso ao relatório analítico e aos demais relatórios.</small>
              </span>
            </label>

            <label v-if="$store.state.user.lzv2 && $store.state.user.lzv2.validacao" class="permission-card" :class="form.admin ? 'opacity-50' : ''">
              <input id="validacaoAcesso" v-model="form.validacaoAcesso" type="checkbox" :disabled="form.admin" :class="checkboxClass" />
              <span>
                <strong>Validação WhatsApp</strong>
                <small>Permite upload de campanhas de validação.</small>
              </span>
            </label>

            <label v-if="!$store.state.user.operador" class="permission-card">
              <input id="configuracaoAcesso" v-model="form.configuracaoAcesso" type="checkbox" :class="checkboxClass" />
              <span>
                <strong>Configuração da conta</strong>
                <small>Acesso à tela de configuração da conta.</small>
              </span>
            </label>
          </div>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button type="submit" class="h-11 rounded-xl bg-blue-500 px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-600 hover:shadow-md">Salvar operador</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
const ESTADO_SEGURO_SEM_SUPERVISOR = {
  admin: false,
  centrocusto: null,
  configuracaoAcesso: false,
  uploadCampanha: false,
  relatorioAcesso: false,
  rastrearAcesso: false,
  visualizarChat: false,
  visualizarFiltrosChat: false,
  permitirMoverChat: false,
  chatAcesso: false,
  validacaoAcesso: false
};
const CAMPOS_ALTERADOS_PELO_SUPERVISOR = Object.keys(ESTADO_SEGURO_SEM_SUPERVISOR);

export default {
  data() {
    return {
      inputClass: "h-11 rounded-xl border border-gray-200 bg-white px-4 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100",
      selectClass:
        "h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white bg-none px-4 pr-10 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-300 focus:ring-2 focus:ring-blue-100",
      checkboxClass: "mt-1 h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-200",
      alterarPassword: this.$route.params.id ? false : true,
      centroscusto: [],
      mostrarSenha: false,
      mostrarSenhaConfirmacao: false,
      supervisorPermissionsSnapshot: null,
      form: {
        nome: "",
        email: "",
        telefone: "",
        password: "",
        password2: "",
        uploadCampanha: false,
        validacaoAcesso: false,
        relatorioAcesso: false,
        rastrearAcesso: false,
        visualizarFiltrosChat: false,
        configuracaoAcesso: false,
        admin: false,
        supervisor: false,
        visualizarChat: false,
        chatAcesso: true,
        centrocusto: null,
        permitirMoverChat: true
      }
    };
  },

  computed: {
    contaBluesend() {
      const user = this.$store.state.user || {};
      const contaId = user.operador ? user.usuario : user._id;
      return !!(user.lzv2 && user.lzv2.cliente_bluesend) || String(contaId) === "66eac8eef58389f70fad372d";
    }
  },
  methods: {
    setSupervisor(eventoAlteracao) {
      if (!this.form.supervisor) {
        if (!eventoAlteracao) return;
        Object.assign(this.form, this.supervisorPermissionsSnapshot || ESTADO_SEGURO_SEM_SUPERVISOR);
        this.supervisorPermissionsSnapshot = null;
        return;
      }

      if (eventoAlteracao && !this.supervisorPermissionsSnapshot) {
        this.supervisorPermissionsSnapshot = CAMPOS_ALTERADOS_PELO_SUPERVISOR.reduce((snapshot, campo) => {
          snapshot[campo] = this.form[campo];
          return snapshot;
        }, {});
      }

      this.form.admin = true;
      this.setAdmin();
      this.form.admin = false;
      this.form.supervisor = true;
      this.form.centrocusto = null;
      this.form.configuracaoAcesso = false;
    },
    normalizeForm() {
      this.form.nome = String(this.form.nome || "").trim();
      this.form.email = String(this.form.email || "").trim();
      this.form.telefone = String(this.form.telefone || "").trim();
      this.form.password = String(this.form.password || "");
      this.form.password2 = String(this.form.password2 || "");
    },

    async save() {
      this.normalizeForm();
      this.setSupervisor();

      if (!(this.$store.state.user.lzv2 && this.$store.state.user.lzv2.respostas)) {
        this.form.chatAcesso = false;
        this.form.visualizarChat = false;
      }

      if (!(this.$store.state.user.lzv2 && this.$store.state.user.lzv2.validacao)) {
        this.form.validacaoAcesso = false;
      }

      if (!this.form.email) {
        return this.$vToastify.error("Digite o email");
      }

      if (this.$store.state.user.lzv2 && this.$store.state.user.lzv2.centrocusto && !this.form.centrocusto && !this.form.admin && !this.form.supervisor) {
        return this.$vToastify.error("Digite o centro de custo");
      }

      if (this.alterarPassword && !this.form.password) {
        return this.$vToastify.error("Digite a senha");
      }

      if (this.alterarPassword && this.form.password !== this.form.password2) {
        return this.$vToastify.error("Senhas não coincidem");
      }

      if (!this.form.uploadCampanha && !this.form.chatAcesso && !this.form.visualizarChat) {
        return this.$vToastify.error("Upload e acesso ou visualização ao chat não podem ser falsos ao mesmo tempo");
      }

      const method = this.form._id ? "put" : "post";
      const req = await this.$http[method]("/v1/operador", this.form);

      if (req.data.success) {
        this.$vToastify.success("Salvo com sucesso!");
        this.$router.push({ path: "/operadores" });
        return;
      }

      this.$vToastify.error(req.data.err);
    },

    setAdmin() {
      if (this.form.admin) {
        this.form.supervisor = false;
        this.form.uploadCampanha = true;
        this.form.relatorioAcesso = true;
        this.form.visualizarChat = true;
        this.form.rastrearAcesso = true;
        this.form.visualizarFiltrosChat = true;
        this.form.permitirMoverChat = true;
        this.form.chatAcesso = false;

        if (!this.$store.state.user.operador && !(this.$store.state.user.lzv2 && this.$store.state.user.lzv2.cliente_novavida)) {
          this.form.configuracaoAcesso = true;
        }

        if (this.$store.state.user.lzv2 && this.$store.state.user.lzv2.validacao) {
          this.form.validacaoAcesso = true;
        }
      }
    },

    setchatAcesso() {
      this.form.visualizarChat = false;
    },

    setvisualizarChat() {
      this.form.chatAcesso = false;
    }
  },

  async beforeMount() {
    const id = this.$route.params.id;

    if (id) {
      const req = await this.$http.get(`/v1/operador/${id}`);
      this.form = {
        ...this.form,
        ...req.data
      };
    }

    const centroscustoReq = await this.$http.post("/v1/centrocusto/list", {
      all: true
    });

    this.centroscusto = centroscustoReq.data.data;
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  color: #3b82f6;
  transform: translateY(-50%) rotate(180deg);
}

.permission-card {
  display: flex;
  cursor: pointer;
  gap: 0.75rem;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  background: white;
  padding: 1rem;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
  transition: all 0.2s ease;
}

.permission-card:hover {
  transform: translateY(-2px);
  border-color: #d1d5db;
  box-shadow: 0 4px 6px rgb(0 0 0 / 0.08);
}

.permission-card strong {
  display: block;
  font-size: 0.875rem;
  color: #1f2937;
}

.permission-card small {
  margin-top: 0.25rem;
  display: block;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #6b7280;
}

.eye-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 9999px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: #9ca3af;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition: all 120ms ease;
}

.eye-btn:hover {
  color: #2563eb;
  border-color: rgba(37, 99, 235, 0.12);
  background: #eef6ff;
  transform: translateY(-50%) scale(1.02);
}

.eye-btn:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.08);
}

.eye-btn.active {
  color: #2563eb;
  background: #eaf2ff;
}
</style>
